import { AnimatePresence } from 'framer-motion';
import React, { memo, useEffect, useState } from 'react';

import RichText from 'components/RichText/RichText';
import useOverElement from 'hooks/useOverElement';
import { useCopyStore } from 'store';
import { appState } from 'utils/appState';
import { extractText } from 'utils/exctractText';
import { ROUTES } from 'utils/routes';
import { slugify } from 'utils/slugify';
import { colors } from 'utils/styles/theme';

import * as S from '../AboutPage.styles';

export interface PillarsItem {
  title: string;
  content: string;
}

export interface PillarsSectionProps {
  sectionHeading: string;
  pillars: PillarsItem[];
  buttonLabel: string;
  autoOpenSlug?: string;
}

const PillarsSection: React.FunctionComponent<PillarsSectionProps> = ({
  sectionHeading,
  pillars,
  buttonLabel,
  autoOpenSlug,
  ...rest
}) => {
  const [ref] = useOverElement();
  const { globalSettings } = useCopyStore(({ copy }) => copy);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const hoverTextColor =
    globalSettings?.buttonsHoverColor ||
    globalSettings?.buttonsHoverDefaultColors;

  useEffect(() => {
    if (!autoOpenSlug) return;

    const index = pillars.findIndex(
      pillar => slugify(pillar.title) === autoOpenSlug
    );

    if (index !== -1) {
      setOpenIndex(index);
      setTimeout(() => {
        const el = document.getElementById(`pillar-${index}`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 250);
    }
  }, [autoOpenSlug, pillars]);

  const toggle = (i: number) => {
    const willOpen = i !== openIndex;

    if (willOpen) {
      const text = extractText(pillars[i].title);
      const slug = slugify(text);

      appState.analytics.trackPillarClick(text, slug);
    }

    setOpenIndex(i === openIndex ? null : i);
  };

  return (
    <S.PillarsSectionGrid ref={ref} {...rest}>
      <S.PillarsSectionWrapper>
        {sectionHeading && (
          <RichText
            as="h2"
            size="heading1"
            richText={sectionHeading}
            defaultFormatting
          />
        )}

        {pillars?.map(({ title, content }, i) => {
          const isOpen = openIndex === i;

          return (
            <S.AccItem key={title + i} hoverTextColor={hoverTextColor}>
              <S.AccInner>
                <S.AccHeader onClick={() => toggle(i)}>
                  <S.AccTitleWrapper>
                    <RichText
                      as="h3"
                      size="heading1"
                      richText={title}
                      defaultFormatting
                    />

                    <S.AccLine
                      initial={false}
                      animate={{
                        width: isOpen ? '100%' : '42%',
                        backgroundColor: colors.brightGray,
                      }}
                      transition={{
                        duration: 0.45,
                        ease: isOpen ? [0.04, 0.62, 0.23, 0.98] : 'easeOut',
                      }}
                      style={{ transformOrigin: 'left' }}
                    />
                  </S.AccTitleWrapper>
                </S.AccHeader>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <S.AccContent
                      initial={{ height: 0, opacity: 0, y: -20 }}
                      animate={{ height: 'auto', opacity: 1, y: 0 }}
                      exit={{ height: 0, opacity: 0, y: -20 }}
                      transition={{
                        duration: 0.9,
                        ease: [0.04, 0.62, 0.23, 0.98],
                      }}
                    >
                      <S.AccContentInner>
                        <RichText richText={content} defaultSpacing />
                      </S.AccContentInner>
                    </S.AccContent>
                  )}
                </AnimatePresence>
              </S.AccInner>
            </S.AccItem>
          );
        })}

        {buttonLabel && (
          <S.PillarButtonWrapper>
            <S.PillarButton label={buttonLabel} href={ROUTES.CONTACT} />
          </S.PillarButtonWrapper>
        )}
      </S.PillarsSectionWrapper>
    </S.PillarsSectionGrid>
  );
};

export default memo(PillarsSection);
